export interface ResourceSchedulePositionedItem<TItem, TTemplate = unknown> {
  item: TItem;
  startSlotIndex: number;
  durationSlots: number;
  template: TTemplate | null;
}

interface ResourceScheduleSchedulableItem {
  id: string;
  scheduled_start?: string | null;
  scheduled_end?: string | null;
}

interface BuildResourceSchedulePositionedItemsOptions<
  TItem extends ResourceScheduleSchedulableItem,
  TTemplate = unknown,
> {
  items: readonly TItem[];
  timelineStart: Date;
  timelineEnd: Date;
  intervalMinutes: number;
  isHiddenItem?: (item: TItem) => boolean;
  isAssignedItem?: (item: TItem) => boolean;
  getTemplate?: (item: TItem) => TTemplate | null;
}

export function buildResourceSchedulePositionedItems<
  TItem extends ResourceScheduleSchedulableItem,
  TTemplate = unknown,
>({
  items,
  timelineStart,
  timelineEnd,
  intervalMinutes,
  isHiddenItem,
  isAssignedItem,
  getTemplate,
}: BuildResourceSchedulePositionedItemsOptions<TItem, TTemplate>): Array<ResourceSchedulePositionedItem<TItem, TTemplate>> {
  const seenIds = new Set<string>();

  return items
    .filter((item) => {
      if (!item.scheduled_start || !item.scheduled_end) {
        return false;
      }

      if (seenIds.has(item.id)) {
        return false;
      }

      if (isHiddenItem?.(item)) {
        return false;
      }

      if (isAssignedItem && !isAssignedItem(item)) {
        return false;
      }

      const itemStart = new Date(item.scheduled_start);
      const itemEnd = new Date(item.scheduled_end);

      if (Number.isNaN(itemStart.getTime()) || Number.isNaN(itemEnd.getTime())) {
        return false;
      }

      if (itemEnd <= timelineStart || itemStart >= timelineEnd) {
        return false;
      }

      seenIds.add(item.id);
      return true;
    })
    .map((item) => {
      const start = new Date(item.scheduled_start!);
      const end = new Date(item.scheduled_end!);

      const startMinutes = Math.max(0, (start.getTime() - timelineStart.getTime()) / 1000 / 60);
      const endMinutes = (end.getTime() - timelineStart.getTime()) / 1000 / 60;

      const startSlotIndex = Math.floor(startMinutes / intervalMinutes);
      const durationSlots = Math.max(1, Math.ceil(endMinutes / intervalMinutes) - startSlotIndex);

      return {
        item,
        startSlotIndex,
        durationSlots,
        template: getTemplate ? getTemplate(item) : null,
      };
    });
}